import type { EstimatedBalance } from '@clarity/contract';

/** One account-level ads line of the estimated balance's breakdown. */
export type BalanceAdsLine = EstimatedBalance['breakdown']['ads'][number];

/**
 * The owner-facing label of one ads deduction in the balance breakdown (shared
 * by the dashboard card and the payouts panel so both read the same):
 *  - `reported` — what Talabat Ads reports spent on the days no payout has
 *    settled yet, plus the 16% VAT Talabat adds;
 *  - `estimated_cpc` — days the Ads report doesn't cover yet (today), estimated
 *    per order from the recent paid payouts;
 *  - `deals` — sponsored-deal fees (no report carries them), estimated per order;
 *  - `per_order` — marketing an older export already folded into its earnings;
 *  - `payout` — a legacy per-payout line (older payloads): the payout id.
 */
export function adsLineLabel(line: BalanceAdsLine, fmt: (n: number) => string): string {
  const per = (n: number | null, rate: number | null) =>
    n !== null && rate !== null ? ` · ${n} order${n === 1 ? '' : 's'} × ${fmt(rate)}` : '';
  switch (line.kind) {
    case 'reported':
      return 'Talabat Ads spend (reported, incl. 16% VAT)';
    case 'estimated_cpc':
      return `Ads not reported yet (estimated${per(line.orders, line.ratePerOrder)})`;
    case 'deals':
      return `Sponsored-deal fees (estimated${per(line.orders, line.ratePerOrder)})`;
    case 'per_order':
      return 'Per-order marketing already in the export';
    default:
      return line.externalPayoutId ?? 'Ads';
  }
}
